import {
  Dispatch, SetStateAction, useCallback, useState,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useHeroSnackbar,
  useTable,
  useQueryTableFetch,
  useTranslations,
  useTableLocalization,
  useFilterButton,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  useTablePreserverDecoderCustomConfigMerger,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  TablePreserverCommonInitialTableValues,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  IUseFilterButtonReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TUseTablePreserverDecoderCustomConfig,
  TSetSearchParams,
} from '@uniqkey-frontend/shared-app';
import {
  VaultSecureNotesOrderProperty,
  GetVaultSecureNotesResponseModel,
} from '@uniqkey-backend-organization-web/api-client';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import ReactQueryKeyEnum from '../../../enums/ReactQueryKeyEnum';
import {
  IGetVaultSecureNotesParams,
  TGetVaultSecureNotesMethod,
} from '../../useVaultsAPI/interfaces';
import {
  IEmployeeSecureNotesTabFilterSubmitResult,
// eslint-disable-next-line max-len
} from '../../../pages/EmployeePage/components/EmployeeSecureNotesTab/components/EmployeeSecureNotesTabFilter';
import useVaultsAPI from '../../useVaultsAPI';
import tablePreservationConfigs from '../../../helpers/tablePreservation/configs';

export const REACT_QUERY_SECURE_NOTES_KEY = [ReactQueryKeyEnum.SecureNotes];

interface IPersistentFilters extends Pick<
  IGetVaultSecureNotesParams, 'employeeAccountId' | 'groupId'
> {}

export interface IUseEmployeeGroupSecureNotesTableParams {
  persistentFilters?: IPersistentFilters;
  customPreservationConfig?: ICustomPreservationConfig;
  noDataMessageKey: string;
}

export interface IEmployeeGroupSecureNotesTableRow extends GetVaultSecureNotesResponseModel {
  lastActivity: string | null;
}

export interface IUseEmployeeGroupSecureNotesTableReturn extends
  Omit<IUseTableReturn<IEmployeeGroupSecureNotesTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage' | 'resetSelectedRows'>,
  Omit<IUseQueryTableFetchReturn<TGetVaultSecureNotesMethod>, 'data' | 'isFetchedAfterMount'>,
  IUseFilterButtonReturn
{
  secureNotes: IUseQueryTableFetchReturn<TGetVaultSecureNotesMethod>['data'];
  selectedSecureNotes: IUseTableReturn<IEmployeeGroupSecureNotesTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  searchQuery: IGetVaultSecureNotesParams['searchQuery'];
  setSearchQuery: Dispatch<SetStateAction<IGetVaultSecureNotesParams['searchQuery']>>;
  filterValues: IEmployeeSecureNotesTabFilterSubmitResult;
  setFilterValues: Dispatch<SetStateAction<IEmployeeSecureNotesTabFilterSubmitResult>>;
}

interface ITablePreservationConfigs extends
  Omit<IGetVaultSecureNotesParams,
    'page' | 'pageLength' | 'groupId' | 'employeeAccountId' | 'orderPropertyName' | 'isDescending'
  >,
  Pick<IUseTableReturn<IEmployeeGroupSecureNotesTableRow>, 'columnOrderBy' | 'columnOrderDirection'>
{
  activePage: IGetVaultSecureNotesParams['page'];
  perPage: IGetVaultSecureNotesParams['pageLength'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  searchQuery: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: VaultSecureNotesOrderProperty },
  ),
  columnOrderDirection: true,
  ownership: tablePreservationConfigs.ownershipWithoutUnmanaged,
};

interface ICustomPreservationConfig extends TUseTablePreserverDecoderCustomConfig<
  ITablePreservationConfigs, 'ownership'
> {}

const useEmployeeGroupSecureNotesTable = (
  params: IUseEmployeeGroupSecureNotesTableParams,
): IUseEmployeeGroupSecureNotesTableReturn => {
  const {
    persistentFilters,
    customPreservationConfig,
    noDataMessageKey,
  } = params;

  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const { showError } = useHeroSnackbar();
  const { getVaultSecureNotes } = useVaultsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const preservationConfig = useTablePreserverDecoderCustomConfigMerger<
    ITablePreservationConfigs, ICustomPreservationConfig
  >(PRESERVATION_CONFIGS, customPreservationConfig);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    searchQuery: initialSearchQuery,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
    ownership: initialOwnership,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, preservationConfig);

  const [searchQuery, setSearchQuery] = useState<
    IGetVaultSecureNotesParams['searchQuery']
  >(() => initialSearchQuery);
  const [filterValues, setFilterValues] = useState<
    IEmployeeSecureNotesTabFilterSubmitResult
  >({
    ownership: initialOwnership,
  });

  const { isFilterActive, numberOfActiveFilters } = useFilterButton(filterValues);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedSecureNotes,
    resetTableMethods,
    ...restTableProps
  } = useTable<IEmployeeGroupSecureNotesTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
    resetSelectedRows,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
    setSearchQuery(TablePreserverCommonInitialTableValues.searchQuery);
    setFilterValues({
      ownership: undefined,
    });
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: secureNotes, isLoading, isFetchedAfterMount, total, resetQuery,
  } = useQueryTableFetch({
    queryKey: REACT_QUERY_SECURE_NOTES_KEY,
    queryClient,
    request: getVaultSecureNotes,
    params: {
      page: activePage,
      pageLength: perPage,
      ...filterValues,
      searchQuery,
      orderPropertyName: VaultSecureNotesOrderProperty[
        upperFirst(columnOrderBy) as keyof typeof VaultSecureNotesOrderProperty
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
  });

  const { localization } = useTableLocalization({
    searchQuery,
    isFilterActive,
    noDataMessageKey,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    searchQuery,
    columnOrderBy,
    columnOrderDirection,
    ownership: filterValues.ownership,
  });

  return {
    secureNotes,
    isLoading,
    total,
    resetQuery,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedSecureNotes,
    localization,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps,
  };
};

export default useEmployeeGroupSecureNotesTable;
